import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "motion/react";
import { createPageUrl } from "./utils";
import { cn } from "./lib/utils";
import { Sun, BookOpen, Home, Users, Menu, X, Moon, HelpCircle } from "lucide-react";
import { Button } from "./Components/ui/button";
import { Reveal } from "./Components/motion/Reveal";

const navigationItems = [
  { name: "Home", path: "Home", icon: Home },
  { name: "Founders", path: "Founders", icon: Users },
  { name: "Articles", path: "Articles", icon: BookOpen },
  { name: "Support", path: "Support", icon: HelpCircle },
];

const APP_STORE =
  "https://apps.apple.com/us/app/sunscreened/id6756286578";

export default function Layout({ children, currentPageName }) {
  const pageLabel = currentPageName || "Sunscreened";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  const { scrollYProgress } = useScroll();
  const scrollBarX = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 28,
    mass: 0.12,
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    window.dispatchEvent(new CustomEvent("themechange", { detail: { theme } }));
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const isDark = theme === "dark";

  return (
    <div
      className={`relative min-h-screen overflow-x-hidden ${
        isDark
          ? "bg-[#05070d] text-slate-100"
          : "bg-[#fff8f3] text-slate-900"
      }`}
    >
      <div
        className={`pointer-events-none fixed inset-0 -z-10 ${
          isDark
            ? "bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(251,146,60,0.18),transparent_55%),radial-gradient(ellipse_80%_50%_at_100%_0%,rgba(244,63,94,0.12),transparent_50%),radial-gradient(ellipse_60%_40%_at_0%_100%,rgba(251,191,36,0.08),transparent_45%)]"
            : "bg-[radial-gradient(ellipse_100%_70%_at_50%_-10%,rgba(253,186,116,0.35),transparent_50%),radial-gradient(ellipse_70%_50%_at_100%_20%,rgba(251,113,133,0.2),transparent_45%)]"
        }`}
        aria-hidden
      />

      <style>{`
        :root {
          --primary-50: #FFF7ED;
          --primary-500: #F97316;
          --primary-600: #EA580C;
          --coral-500: #F43F5E;
        }
      `}</style>

      <div className="pointer-events-none fixed inset-x-0 top-0 z-[99] flex justify-center px-2 pt-5 sm:px-4">
      <motion.nav
        className={cn(
          "pointer-events-auto w-full max-w-5xl overflow-hidden transition-[box-shadow,border-radius] duration-300",
          mobileMenuOpen ? "rounded-2xl" : "rounded-full",
          scrolled
            ? isDark
              ? "border border-white/15 bg-[#0a0f1a]/95 shadow-2xl shadow-black/50 backdrop-blur-xl"
              : "border border-orange-200/80 bg-white/95 shadow-xl shadow-orange-200/30 backdrop-blur-xl"
            : isDark
              ? "border border-white/10 bg-[#0a0f1a]/88 shadow-xl shadow-black/40 backdrop-blur-xl"
              : "border border-orange-200/60 bg-white/90 shadow-lg shadow-orange-100/40 backdrop-blur-xl"
        )}
      >
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[3px] origin-left bg-gradient-to-r from-amber-300 via-orange-500 to-rose-500"
          style={{ scaleX: scrollBarX }}
        />
        <div className="relative mx-auto max-w-5xl px-3 sm:px-5">
          <div className="flex min-h-[3.25rem] items-center justify-between py-2 md:min-h-[3.5rem]">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link to={createPageUrl("Home")} className="flex items-center gap-3">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68f7c9965d72f92ed4e7245a/9cd7eeac7_SunScreenDLogojgp.jpg"
                  alt="Sunscreened"
                  className="h-9 w-auto rounded-xl ring-1 ring-white/10 md:h-10"
                />
                <span className="font-display text-lg font-semibold tracking-tight md:text-xl">
                  <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 bg-clip-text text-transparent">
                    Sunscreened
                  </span>
                </span>
              </Link>
            </motion.div>

            <div className="hidden items-center gap-2 md:flex">
              <div
                className={cn(
                  "flex items-center gap-1 rounded-full border px-1 py-1",
                  isDark ? "border-white/10 bg-white/[0.04]" : "border-orange-100/80 bg-white/70"
                )}
              >
                {navigationItems.map((item) => {
                  const active = location.pathname === createPageUrl(item.path);
                  return (
                    <Link key={item.path} to={createPageUrl(item.path)}>
                      <motion.span
                        className={cn(
                          "rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                          active
                            ? isDark
                              ? "bg-orange-500/15 text-amber-200"
                              : "bg-orange-50 text-orange-700"
                            : isDark
                              ? "text-slate-300 hover:bg-white/5 hover:text-amber-200"
                              : "text-slate-600 hover:bg-orange-50/80 hover:text-orange-700"
                        )}
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {item.name}
                      </motion.span>
                    </Link>
                  );
                })}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={cn(
                  "rounded-full",
                  isDark
                    ? "text-slate-300 hover:bg-white/5 hover:text-amber-200"
                    : "text-slate-600 hover:bg-orange-50"
                )}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <motion.a
                href={APP_STORE}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button className="rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 px-5 text-white shadow-lg shadow-orange-500/25 hover:from-amber-500 hover:via-orange-600 hover:to-rose-600">
                  Download
                </Button>
              </motion.a>
            </div>

            <div className="flex items-center gap-2 md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={cn("rounded-full", isDark ? "text-slate-300" : "text-slate-700")}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className={isDark ? "text-slate-300" : "text-slate-700"}
                onClick={() => setMobileMenuOpen((o) => !o)}
                aria-expanded={mobileMenuOpen}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {mobileMenuOpen && (
              <motion.div
                key="mobile-nav"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className={`flex flex-col gap-2 border-t py-4 md:hidden ${
                  isDark ? "border-white/10" : "border-orange-100"
                }`}
              >
                {navigationItems.map((item, i) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * i }}
                  >
                    <Link
                      to={createPageUrl(item.path)}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-base font-medium ${
                        location.pathname === createPageUrl(item.path)
                          ? isDark
                            ? "bg-orange-500/15 text-amber-200"
                            : "bg-orange-50 text-orange-700"
                          : isDark
                            ? "text-slate-200 hover:bg-white/5"
                            : "text-slate-700 hover:bg-orange-50/60"
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                <a
                  href={APP_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 w-full"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white">
                    Download app
                  </Button>
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
      </div>

      <main
        className="pt-24 md:pt-28"
        aria-label={pageLabel}
      >
        {children}
      </main>

      <footer
        className={`relative mt-24 overflow-hidden border-t ${
          isDark
            ? "border-white/5 bg-[#03050a] text-slate-100"
            : "border-orange-100/80 bg-gradient-to-b from-white to-orange-50/40 text-slate-900"
        }`}
      >
        <div
          className={`pointer-events-none absolute inset-0 opacity-40 ${
            isDark
              ? "bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.12),transparent_40%)]"
              : "bg-[radial-gradient(circle_at_80%_0%,rgba(253,186,116,0.35),transparent_45%)]"
          }`}
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            <Reveal>
              <div className="flex items-center gap-3">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68f7c9965d72f92ed4e7245a/9cd7eeac7_SunScreenDLogojgp.jpg"
                  alt="Sunscreened"
                  className="h-11 w-auto rounded-xl ring-1 ring-black/5"
                />
                <span className="font-display text-xl font-semibold">Sunscreened</span>
              </div>
              <p
                className={`mt-4 max-w-sm text-sm leading-relaxed ${
                  isDark ? "text-slate-400" : "text-slate-600"
                }`}
              >
                AI-powered skin monitoring and UV-aware guidance so you can enjoy daylight with
                confidence.
              </p>
            </Reveal>

            <Reveal>
              <h3 className="font-display text-lg font-semibold">Explore</h3>
              <div className="mt-4 flex flex-col gap-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={createPageUrl(item.path)}
                    className={`text-sm transition-colors ${
                      isDark
                        ? "text-slate-400 hover:text-amber-200"
                        : "text-slate-600 hover:text-orange-700"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <h3 className="font-display text-lg font-semibold">Legal & contact</h3>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to={createPageUrl("Privacy")}
                  className={`text-sm transition-colors ${
                    isDark ? "text-slate-400 hover:text-amber-200" : "text-slate-600 hover:text-orange-700"
                  }`}
                >
                  Privacy Policy
                </Link>
                <Link
                  to={createPageUrl("Terms")}
                  className={`text-sm transition-colors ${
                    isDark ? "text-slate-400 hover:text-amber-200" : "text-slate-600 hover:text-orange-700"
                  }`}
                >
                  Terms &amp; Conditions
                </Link>
              </div>
              <p
                className={`mt-4 text-sm ${
                  isDark ? "text-slate-500" : "text-slate-600"
                }`}
              >
                Questions about sun safety or partnerships?
              </p>
              <a href="mailto:sunscreened26@gmail.com" className="mt-3 inline-block">
                <Button
                  className={`rounded-full ${
                    isDark
                      ? "bg-gradient-to-r from-amber-500 to-orange-600 text-slate-950 hover:opacity-95"
                      : "bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:opacity-95"
                  }`}
                >
                  Email us
                </Button>
              </a>
            </Reveal>
          </div>

          <div
            className={`mt-12 border-t pt-8 text-center text-xs ${
              isDark ? "border-white/10 text-slate-500" : "border-orange-100 text-slate-500"
            }`}
          >
            <p>© {new Date().getFullYear()} Sunscreened. Built for safer sun habits.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
